import { useTranslation } from 'react-i18next';

export default function AboutSection() {
  const { t } = useTranslation();

  const showaFeatures = [
    {
      icon: 'ri-phone-line',
      title: 'Rotary Phone',
      description: 'A working black rotary phone from the 1970s - a nostalgic touch of Showa communication'
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Chabudai Table',
      description: 'Traditional low dining table where families gathered for meals and conversations'
    },
    {
      icon: 'ri-tv-2-line',
      title: 'Vintage Appliances',
      description: 'Authentic Showa-era electronics and home goods that tell stories of the past'
    },
    {
      icon: 'ri-door-line',
      title: 'Tatami & Shoji',
      description: 'Natural light filtering through traditional paper screens onto soft tatami mats'
    }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-showa-cream to-white relative overflow-hidden">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 opacity-5" 
           style={{
             backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234A3728' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
           }}>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-showa-red font-retro text-sm tracking-widest border-b-2 border-showa-red pb-1">
              ABOUT THE EXPERIENCE
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-showa-brown mb-6 retro-text-shadow">
            A Journey Back to
            <span className="block text-showa-red mt-2">Showa Era Japan</span>
          </h2>
          <p className="text-lg md:text-xl text-showa-sepia max-w-3xl mx-auto leading-relaxed">
            Step through our doors and travel back to 1970s Tokyo. Every corner of KOMINKA tells a story 
            of a bygone era - from the warm glow of vintage light bulbs to the gentle creak of wooden floors.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left: Film-style Photo */}
          <div className="relative film-grain">
            <div className="film-border overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/f2ffe13c35cab9cbea0d3ae40be7b068.jpg"
                alt="Showa Era Interior"
                className="w-full h-full object-cover sepia-filter"
              />
            </div>
            {/* Film Caption */}
            <div className="absolute -bottom-4 -right-4 bg-showa-darkBrown text-showa-cream px-6 py-3 font-retro text-xs tracking-wider shadow-lg">
              TOKYO, 1970s STYLE
            </div>
          </div>

          {/* Right: Story Content */}
          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm p-8 border-l-4 border-showa-gold shadow-lg">
              <h3 className="text-2xl font-serif font-bold text-showa-brown mb-4">
                Like Walking Into a Japanese Film
              </h3>
              <p className="text-showa-sepia leading-relaxed mb-4">
                Our guests often say they feel like they've stepped into a classic Japanese movie. 
                The authentic Showa-era atmosphere isn't recreated - it's preserved. This is how 
                Japanese families truly lived during the golden age of the 1970s.
              </p>
              <p className="text-showa-sepia leading-relaxed">
                Located in the quiet residential neighborhood of Magome, Ota-ku, our kominka offers 
                a rare glimpse into traditional Tokyo life, just 25 minutes from Haneda Airport.
              </p>
            </div>

            {/* Quote Box */}
            <div className="relative bg-showa-red/10 p-6 border-2 border-showa-red/30">
              <i className="ri-double-quotes-l text-4xl text-showa-red/30 absolute top-2 left-2"></i>
              <p className="text-showa-brown italic font-serif text-lg pl-8 pr-4">
                "It felt like time travel. Every detail, from the rotary phone to the tatami rooms, 
                transported us to another era. An unforgettable experience."
              </p>
              <p className="text-right text-sm text-showa-sepia mt-3 font-retro">
                - Guest from California, 2024
              </p>
            </div>
          </div>
        </div>

        {/* Showa Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {showaFeatures.map((feature, index) => (
            <div
              key={index}
              className="group bg-white hover:bg-showa-cream/50 p-6 border-2 border-showa-warmGray/20 hover:border-showa-gold transition-all duration-300 shadow-md hover:shadow-xl"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-showa-red/10 text-showa-red mb-4 group-hover:bg-showa-red group-hover:text-white transition-all duration-300">
                <i className={`${feature.icon} text-3xl`}></i>
              </div>
              <h4 className="text-xl font-serif font-bold text-showa-brown mb-3">
                {feature.title}
              </h4>
              <p className="text-showa-sepia text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-showa-darkBrown text-showa-cream px-8 py-4 shadow-xl">
            <p className="font-serif text-lg mb-2">
              Experience the warmth and nostalgia of Showa-era Japan
            </p>
            <p className="text-showa-gold font-retro text-sm tracking-widest">
              WHOLE HOUSE RENTAL • AUTHENTIC VINTAGE LIVING
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}